import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Check, Loader2, MapPin, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../../services/adminService';

const ONE_OFF = '';

// Correcting which of a client's sites an order is for. A correction of the
// record, not a change of order: no amounts move and nobody is notified.
export const OrderLocationDialog = ({ open, onOpenChange, orderId, onSaved }) => {
  const [choices, setChoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(ONE_OFF);
  const [address, setAddress] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getOrderLocationChoices(orderId);
      setChoices(data);
      setSelected(data.location_id || ONE_OFF);
      setAddress(data.delivery_address || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read this order’s delivery sites');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { if (open && orderId) load(); }, [open, orderId, load]);

  const pickSite = (site) => {
    setSelected(site.id);
    setAddress(site.address || '');
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminService.setOrderLocation(orderId, selected || null, address);
      toast.success(result.location_name
        ? `Now filed as ${result.location_name}`
        : 'Saved as a one-off address with no site');
      onSaved?.(result);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the delivery location');
    } finally {
      setSaving(false);
    }
  };

  const sites = choices?.sites || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="order-location-dialog">
        <DialogHeader>
          <DialogTitle>Delivery location</DialogTitle>
          <DialogDescription>
            Which of {choices?.client_name || 'this client'}&apos;s sites this order is for. It
            follows the order onto the invoice and the staff screens — correcting it here corrects
            them too, and changes no amounts.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
        ) : (
          <div className="space-y-4">
            {sites.length === 0 ? (
              <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
                data-testid="order-location-no-sites">
                No delivery sites are on file for this client yet, so this order can only carry a
                typed address. Sites are added on the client&apos;s record.
              </p>
            ) : (
              <div className="space-y-1.5">
                <Label>Saved sites</Label>
                {sites.map((site) => (
                  <button
                    key={site.id}
                    type="button"
                    onClick={() => pickSite(site)}
                    className={`flex w-full items-start gap-2 rounded-lg border p-3 text-left transition-colors ${
                      selected === site.id
                        ? 'border-amber-400 bg-amber-50'
                        : 'border-gray-200 hover:border-amber-200 hover:bg-amber-50/40'
                    }`}
                    data-testid={`order-location-site-${site.id}`}
                  >
                    <MapPin className={`mt-0.5 h-4 w-4 shrink-0 ${selected === site.id ? 'text-amber-600' : 'text-gray-400'}`} />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{site.name}</span>
                        {site.is_default && (
                          <Badge variant="outline" className="text-[10px]">Default</Badge>
                        )}
                        {choices.suggested_location_id === site.id && (
                          <Badge variant="outline" className="border-amber-300 bg-amber-100 text-[10px] text-amber-900">
                            Matches this order&apos;s address
                          </Badge>
                        )}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-600">{site.address}</span>
                    </span>
                    {selected === site.id && <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSelected(ONE_OFF)}
                  className={`flex w-full items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                    selected === ONE_OFF
                      ? 'border-gray-400 bg-gray-50 text-gray-900'
                      : 'border-dashed border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="order-location-one-off"
                >
                  {selected === ONE_OFF && <Check className="h-4 w-4 shrink-0 text-gray-600" />}
                  Keep a one-off address — no saved site
                </button>
              </div>
            )}

            <div>
              <Label htmlFor="order-location-address">Delivery address</Label>
              <Textarea
                id="order-location-address"
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="mt-1"
                data-testid="order-location-address"
              />
              <p className="mt-1 text-xs text-gray-500">
                Choosing a site fills this in from the client&apos;s record; you can still adjust it.
              </p>
            </div>

            {choices?.client_id && (
              <Link
                to={`/admin/clients?client=${choices.client_id}&tab=locations`}
                className="inline-flex items-center gap-1 text-sm text-amber-700 hover:underline"
                data-testid="order-location-add-site-link"
              >
                Add or edit this client&apos;s sites <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="order-location-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={loading || saving}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="order-location-save"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderLocationDialog;
